import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from 'src/components/Iconify';
import { adminApi } from 'src/utils/adminApi';

function PasswordAdornment({ visible, onToggle }) {
  return (
    <InputAdornment position="end">
      <IconButton edge="end" onClick={onToggle} aria-label={visible ? 'Hide password' : 'Show password'}>
        <Iconify icon={visible ? 'eva:eye-off-fill' : 'eva:eye-fill'} />
      </IconButton>
    </InputAdornment>
  );
}

PasswordAdornment.propTypes = {
  visible: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired
};

export default function AdminPasswordDialog({ open, onClose }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const resetFields = () => {
    setCurrentPassword('');
    setPassword('');
    setPasswordConfirmation('');
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (password !== passwordConfirmation) {
      setError('New password and confirmation do not match.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const result = await adminApi.updateMyPassword({
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation
      });
      setCurrentPassword('');
      setPassword('');
      setPasswordConfirmation('');
      setSuccess(result.message || 'Password updated.');
      setTimeout(() => onClose(), 800);
    } catch (err) {
      setError(err.message || 'Could not change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onEnter: resetFields }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Change password</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Current password"
            type={showCurrent ? 'text' : 'password'}
            fullWidth
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <PasswordAdornment visible={showCurrent} onToggle={() => setShowCurrent((v) => !v)} />
              )
            }}
          />
          <TextField
            label="New password"
            type={showNew ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: <PasswordAdornment visible={showNew} onToggle={() => setShowNew((v) => !v)} />
            }}
          />
          <TextField
            label="Confirm new password"
            type={showConfirm ? 'text' : 'password'}
            fullWidth
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            InputProps={{
              endAdornment: (
                <PasswordAdornment visible={showConfirm} onToggle={() => setShowConfirm((v) => !v)} />
              )
            }}
          />
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={onClose} disabled={loading}>
          Cancel
        </LoadingButton>
        <LoadingButton variant="contained" loading={loading} onClick={handleSave}>
          Update password
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

AdminPasswordDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func
};
